import { findNode, findNodePath, nodeIdsInTreeOrder, pruneDescendantNodeIds, useCorpusStore, type CorpusNode } from '../../stores/corpus'
import { useSettingsStore } from '../../stores/settings'
import { syncEngine } from '../sync/syncEngine'
import type { ContextMenuItem } from '../../components/app/ContextMenu.vue'
import { recordCorpusUndo } from './corpusUndo'

// Actions on corpus nodes shared by the Corpus Panel and the folder overview,
// so both surfaces trash, move, rename and restyle items the same way.

export interface NodeSettingsPayload {
  icon: string | null
  color: string | null
  aliases: string[]
  includeInExport: boolean
  includeInWordCount: boolean
  subtype?: 'desk' | 'folder' | 'page'
  namingScheme?: string | null
  /** 'YYYY-MM-DD', null to clear, or left out when the dialog did not show it. */
  due?: string | null
}

export async function syncMarkdownMirror() {
  const corpus = useCorpusStore()
  const settings = useSettingsStore()
  if (settings.markdownMirrorPath && (window as any).api?.syncMarkdownMirror) {
    await (window as any).api.syncMarkdownMirror(settings.markdownMirrorPath, corpus.tree)
  }
}

function countPages(node: CorpusNode): number {
  return (node.subtype === 'page' ? 1 : 0) + (node.children || []).reduce((sum, child) => sum + countPages(child), 0)
}

/** Detaches the open page from sync when it is about to leave with `ids`. */
function releaseActivePage(ids: string[]) {
  const corpus = useCorpusStore()
  const activeId = corpus.activePageId
  if (!activeId) return
  const affected = ids.some((id) => {
    if (id === activeId) return true
    const node = corpus.findNode(id)
    return !!(node?.children && findNode(node.children, activeId))
  })
  if (affected) syncEngine.disconnectDocument(activeId)
}

function quoted(name: string) {
  return `“${name}”`
}

/**
 * Moves items and everything inside them to Trash straight away, with an Undo
 * on the notice (Trash keeps them restorable anyway, so no confirmation).
 * Resolves false when nothing was trashed; throws when the move fails.
 */
export async function trashNodesWithUndo(ids: string[]): Promise<boolean> {
  const corpus = useCorpusStore()
  const topLevelIds = pruneDescendantNodeIds(corpus.tree, ids)
  if (topLevelIds.length === 0) return false
  const activeBefore = corpus.activePageId
  releaseActivePage(topLevelIds)
  const entries = await corpus.trashNodes(topLevelIds)
  await syncMarkdownMirror()
  if (entries.length === 0) return false
  const activeAfter = corpus.activePageId

  const pageCount = entries.reduce((sum, entry) => sum + countPages(entry.node), 0)
  const message = entries.length === 1
    ? `Moved ${quoted(entries[0].node.name)}${entries[0].node.subtype !== 'page' && pageCount > 0
      ? ` and ${pageCount} ${pageCount === 1 ? 'document' : 'documents'}`
      : ''} to Trash`
    : `Moved ${entries.length} items to Trash`

  recordCorpusUndo(message, async () => {
    // Each entry remembers its index after the earlier ones were removed, so
    // restoring newest-first puts siblings back in their original order.
    for (const entry of [...entries].reverse()) await corpus.restoreTrashEntry(entry.node.id)
    await syncMarkdownMirror()
    // Reopen the page that was open, unless the writer has moved on since.
    const reopened = activeBefore && entries.some(entry =>
      entry.node.id === activeBefore || findNode(entry.node.children ?? [], activeBefore))
    if (reopened && corpus.activePageId === activeAfter) corpus.activePageId = activeBefore
  }, { announce: true })
  return true
}

export function trashNodeWithUndo(node: CorpusNode): Promise<boolean> {
  return trashNodesWithUndo([node.id])
}

/** Renames an item, remembering the old name for undo. */
export function renameNodeWithUndo(id: string, name: string) {
  const corpus = useCorpusStore()
  const previous = corpus.findNode(id)?.name
  if (previous === undefined || previous === name) return
  corpus.renameNode(id, name)
  recordCorpusUndo(`Renamed ${quoted(previous)} to ${quoted(name)}`, () => corpus.renameNode(id, previous))
}

/**
 * Runs `perform`, which moves some of `ids`, and records how to put them back.
 * A move into a different folder is announced with an Undo button, since a
 * drop on the wrong row is easy to miss; a reorder in place is not.
 */
export function moveWithUndo(ids: string[], perform: () => void) {
  const corpus = useCorpusStore()
  const before = corpus.captureNodePlacements(ids)
  perform()
  const after = new Map(corpus.captureNodePlacements(ids).map(placement => [placement.id, placement]))
  const moved = before.filter((placement) => {
    const now = after.get(placement.id)
    return now && (now.parentId !== placement.parentId || now.position !== placement.position)
  })
  if (moved.length === 0) return

  const destinations = new Set(moved.map(placement => after.get(placement.id)!.parentId))
  const changedParent = moved.some(placement => after.get(placement.id)!.parentId !== placement.parentId)
  const subject = moved.length === 1 ? quoted(corpus.findNode(moved[0].id)?.name ?? 'item') : `${moved.length} items`
  let message = `Reordered ${subject}`
  if (changedParent && destinations.size === 1) {
    const [destinationId] = destinations
    const destination = destinationId ? corpus.findNode(destinationId) : null
    message = `Moved ${subject} into ${destination ? quoted(destination.name) : 'the top level'}`
  } else if (changedParent) {
    message = `Moved ${subject}`
  }

  recordCorpusUndo(message, async () => {
    corpus.restoreNodePlacements(moved)
    await syncMarkdownMirror()
  }, { announce: changedParent })
}

/** Moves items onto the tree header's target: the parent of the current desk, or the top level. */
export function moveNodesToHeaderParent(ids: string[]) {
  const corpus = useCorpusStore()
  const topLevelIds = pruneDescendantNodeIds(corpus.tree, nodeIdsInTreeOrder(corpus.tree, ids))
  // Inside a desk each item lands just after the desk; at the top level each is appended.
  const ordered = corpus.focusStack.length > 0 ? [...topLevelIds].reverse() : topLevelIds
  moveWithUndo(topLevelIds, () => {
    for (const id of ordered) corpus.moveNodeToHeaderParent(id)
  })
}

/** Saves what the Document/Folder/Desk Settings popover returned, changing only what differs. */
export function applyNodeSettings(node: CorpusNode, payload: NodeSettingsPayload) {
  const corpus = useCorpusStore()
  const settings = useSettingsStore()
  if ((node.icon ?? null) !== payload.icon) corpus.setNodeIcon(node.id, payload.icon)
  if ((node.color ?? null) !== payload.color) corpus.setNodeColor(node.id, payload.color)
  if (payload.due !== undefined && (node.due ?? null) !== payload.due) corpus.setNodeDue(node.id, payload.due)
  if (node.subtype !== 'page') {
    if (payload.subtype && payload.subtype !== 'page' && payload.subtype !== node.subtype) {
      corpus.setNodeSubtype(node.id, payload.subtype)
    }
    if (payload.namingScheme !== undefined
      && payload.namingScheme !== (settings.pageNamingAssignments[node.id] ?? null)) {
      settings.setContainerNamingScheme(node.id, payload.namingScheme)
    }
    return
  }
  const aliasesChanged = JSON.stringify(node.aliases || []) !== JSON.stringify(payload.aliases)
  const exportChanged = (node.includeInExport !== false) !== payload.includeInExport
  const wordCountChanged = (node.includeInWordCount !== false) !== payload.includeInWordCount
  if (aliasesChanged || exportChanged || wordCountChanged) {
    corpus.setDocumentMetadata(node.id, {
      aliases: payload.aliases,
      includeInExport: payload.includeInExport,
      includeInWordCount: payload.includeInWordCount,
    })
  }
}

export async function moveNodesTo(ids: string[], targetId: string | null) {
  const corpus = useCorpusStore()
  const topLevelIds = pruneDescendantNodeIds(corpus.tree, nodeIdsInTreeOrder(corpus.tree, ids))
  moveWithUndo(topLevelIds, () => {
    for (const id of topLevelIds) corpus.moveNode(id, targetId, 'inside')
  })
  if (targetId) corpus.revealNode(targetId)
  await syncMarkdownMirror()
}

/**
 * Folders and desks in the current view that can receive every node in
 * `ids`, indented to show nesting. The view's own root comes first.
 */
export function moveTargetItems(ids: string[]): ContextMenuItem[] {
  const corpus = useCorpusStore()
  const moving = ids.map(id => corpus.findNode(id)).filter(Boolean) as CorpusNode[]
  const parentIds = new Set(moving.map(node => {
    const path = findNodePath(corpus.tree, node.id)
    return path.length > 1 ? path[path.length - 2].id : null
  }))
  const rootId = corpus.currentDesk?.id ?? null
  const indent = (depth: number) => ' '.repeat(depth)
  const items: ContextMenuItem[] = [{
    label: corpus.currentDeskName ? `${corpus.currentDeskName} (top level)` : 'Top level',
    icon: corpus.currentDeskName ? 'desk' : undefined,
    disabled: parentIds.size === 1 && parentIds.has(rootId),
    action: () => { void moveNodesTo(ids, null) },
  }]
  const walk = (nodes: CorpusNode[], depth: number) => {
    for (const node of nodes) {
      if (node.subtype === 'page' || moving.some(item => item.id === node.id)) continue
      items.push({
        label: `${indent(depth)}${node.name}`,
        icon: node.subtype === 'desk' ? 'desk' : 'folder',
        disabled: parentIds.size === 1 && parentIds.has(node.id),
        action: () => { void moveNodesTo(ids, node.id) },
      })
      // Nested desks are entered rather than expanded, but can still be targets.
      if (node.children) walk(node.children, depth + 1)
    }
  }
  walk(corpus.visibleTree, 0)
  return items
}
